import React, { useCallback, useState } from "react";
import { ColumnType, FilterOperator, filterOperatorFromJSON } from "build-ts/enums";
import { ColumnInfo, FilterProto } from "build-ts/metadata";
import { getColumnValues } from "services/DataModelService";
import showToast from "hooks/useCustomToast";
import FiltersComponent from "modules/metric/components/Filters";
import { DateFilter } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";

interface FiltersContainerProps {
  columns: ColumnInfo[];
  columnValues?: string[];
  selectedFilterColumn: ColumnInfo | null;
  selectedOperator?: FilterOperator;
  inputValue: string;
  selectedColumnValues: string[];
  selectedFilterColumns: FilterProto[];
  dateFilter: DateFilter | undefined;
  dateFilterCategory: DateFilterCategory | undefined;
  compact?: boolean;

  setColumnValues: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedColumnValues: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedFilterColumn: React.Dispatch<React.SetStateAction<ColumnInfo | null>>;
  setSelectedOperator: React.Dispatch<React.SetStateAction<FilterOperator | undefined>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  setSelectedFilterColumns: React.Dispatch<React.SetStateAction<FilterProto[]>>;
  setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
  setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;
}

const FiltersContainer: React.FC<FiltersContainerProps> = ({
  columns,
  columnValues,
  selectedFilterColumn,
  selectedOperator,
  inputValue,
  selectedColumnValues,
  selectedFilterColumns,
  dateFilter,
  dateFilterCategory,
  compact,
  setColumnValues,
  setSelectedColumnValues,
  setSelectedFilterColumn,
  setSelectedOperator,
  setInputValue,
  setSelectedFilterColumns,
  setDateFilter,
  setDateFilterCategory,
}) => {
  const [filterLoading, setFilterLoading] = useState(false);

  const handleFilterColumnChange = useCallback(
    async (columnId: string) => {
      const column = columns.find(c => c.header?.id === columnId) ?? null;

      // Set the column immediately so the UI reflects the selection and the
      // loader can appear before the attribute values fetch resolves.
      setSelectedFilterColumn(column);
      setSelectedColumnValues([]);

      if (column?.column_type === ColumnType.ATTRIBUTE) {
        try {
          setFilterLoading(true);
          const fetchedValues = await getColumnValues(columnId);
          setColumnValues(fetchedValues);
        } catch {
          showToast("Error fetching column values.", "", "error");
        } finally {
          setFilterLoading(false);
        }
      }
    },
    [columns, setColumnValues, setSelectedFilterColumn, setSelectedColumnValues]
  );

  const handleOperatorChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedOperator(filterOperatorFromJSON(event.target.value));
    },
    [setSelectedOperator]
  );

  const handleValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      setSelectedColumnValues([]);
    },
    [setInputValue, setSelectedColumnValues]
  );

  const handleAddFilterColumn = useCallback(() => {
    if (!selectedFilterColumn) {
      showToast("Please select all fields.", "", "error");
      return;
    }

    const isTemporal = selectedFilterColumn.column_type === ColumnType.TEMPORAL;

    if (isTemporal) {
      if (!dateFilter) {
        showToast("Please select all fields.", "", "error");
        return;
      }
      const newFilter = FilterProto.create({
        logical_column_header: selectedFilterColumn.header!,
        values: [],
        date_filter: dateFilter,
      });
      setSelectedFilterColumns((prev = []) => [...prev, newFilter]);
    } else {
      if (selectedOperator === undefined) {
        showToast("Please select all fields.", "", "error");
        return;
      }
      const hasValues = inputValue.trim() || selectedColumnValues.length > 0;
      if (!hasValues) {
        showToast("Please select all fields.", "", "error");
        return;
      }
      const values = inputValue ? inputValue.split(",").map(v => v.trim()) : selectedColumnValues;
      const newFilter = FilterProto.create({
        logical_column_header: selectedFilterColumn.header!,
        operator: selectedOperator,
        values,
      });
      setSelectedFilterColumns((prev = []) => [...prev, newFilter]);
    }

    setSelectedFilterColumn(null);
    setSelectedOperator(undefined);
    setInputValue("");
    setSelectedColumnValues([]);
  }, [
    selectedFilterColumn,
    selectedOperator,
    inputValue,
    selectedColumnValues,
    dateFilter,
    setSelectedFilterColumns,
    setSelectedFilterColumn,
    setSelectedOperator,
    setInputValue,
    setSelectedColumnValues,
  ]);

  const handleToggle = useCallback(
    (value: string) => {
      setSelectedColumnValues(prev => {
        const idx = prev.indexOf(value);
        if (idx === -1) return [...prev, value];
        return prev.filter((_, i) => i !== idx);
      });
    },
    [setSelectedColumnValues]
  );

  const handleDeleteFilterColumn = useCallback(
    (indexToDelete: number) => {
      setSelectedFilterColumns(prev => prev.filter((_, index) => index !== indexToDelete));
    },
    [setSelectedFilterColumns]
  );

  return (
    <FiltersComponent
      columns={columns}
      columnValues={columnValues}
      selectedFilterColumn={selectedFilterColumn}
      selectedOperator={selectedOperator}
      inputValue={inputValue}
      selectedColumnValues={selectedColumnValues}
      selectedFilterColumns={selectedFilterColumns}
      dateFilter={dateFilter}
      dateFilterCategory={dateFilterCategory}
      isLoading={filterLoading}
      compact={compact}
      setDateFilter={setDateFilter}
      setDateFilterCategory={setDateFilterCategory}
      handleFilterColumnChange={handleFilterColumnChange}
      handleOperatorChange={handleOperatorChange}
      handleValueChange={handleValueChange}
      handleAddFilterColumn={handleAddFilterColumn}
      handleToggle={handleToggle}
      handleDeleteFilterColumn={handleDeleteFilterColumn}
    />
  );
};

export default FiltersContainer;
